/**
 * Landing Page JavaScript Module
 * File: landing-page.js
 * Location: Theme/src/js/custom/pages/landing-page.js
 *
 * Security Compliance: VAPT M25 1806 Reserve Bank of Fiji
 * - External script file (CSP Compliant - CRITICAL-003)
 * - No eval() or unsafe operations (XSS Prevention - MEDIUM-004)
 * - Content sanitization for any dynamic content
 * - Input validation for all user interactions
 *
 * Clean Code Principles Applied:
 * - Single Responsibility Principle (SRP)
 * - Module pattern for encapsulation
 * - Descriptive function names
 * - Proper error handling
 * - No magic numbers/strings (constants defined)
 * - DRY Principle: No code duplication
 *
 * JavaScript Class Declaration Style:
 * - Function-type classes as per C# .NET Core Clean Architecture Guide
 * - Expose public methods through return statement
 * - Use const and let (no var)
 */

"use strict";

/**
 * Landing Page Module
 * Handles landing page initialization and user interactions
 * @returns {Object} Public API methods
 */
const LandingPage = (function () {
    // ============================================
    // PRIVATE CONSTANTS
    // Clean Code: Named constants prevent magic strings
    // ============================================

    const SELECTORS = Object.freeze({
        landingContainer: '.landing-page-container',
        actionCards: '.landing-card-link',
        quickLinks: '.landing-quick-link',
        heroSection: '.landing-hero',
        heroBadge: '.landing-hero-badge',
        heroBadgeLogo: '.landing-hero-badge-logo',
        limitInfo: '.landing-hero-limit-info',
        stepCards: '.landing-step-card'
    });

    const ANIMATION = Object.freeze({
        fadeInDelay: 100,      // milliseconds between card animations
        fadeInDuration: 300    // milliseconds for fade animation
    });

    const ANALYTICS_EVENTS = Object.freeze({
        cardClick: 'landing_card_click',
        linkClick: 'landing_link_click',
        pageView: 'landing_page_view'
    });

    const CSS_CLASSES = Object.freeze({
        focusVisible: 'js-focus-visible-fallback',
        animated: 'animated',
        visible: 'visible'
    });

    // ============================================
    // PRIVATE STATE
    // ============================================

    let isInitialized = false;

    // ============================================
    // PRIVATE METHODS - INITIALIZATION
    // ============================================

    /**
     * Initializes the landing page module
     * Clean Code: Single entry point for initialization
     * @returns {void}
     */
    const init = function () {
        if (isInitialized) {
            console.warn('LandingPage: Module already initialized');
            return;
        }

        try {
            bindEventListeners();
            initializeAccessibility();
            initializeAnimations();
            initializeLogoFallback();
            isInitialized = true;
            console.info('LandingPage: Module initialized successfully');
        } catch (error) {
            handleError('init', error);
        }
    };

    // ============================================
    // PRIVATE METHODS - EVENT HANDLING
    // ============================================

    /**
     * Binds event listeners to page elements
     * VAPT Compliance: No inline event handlers
     * @returns {void}
     */
    const bindEventListeners = function () {
        // Action card click tracking
        const actionCards = document.querySelectorAll(SELECTORS.actionCards);
        actionCards.forEach(function (card) {
            card.addEventListener('click', handleCardClick);
            card.addEventListener('keydown', handleCardKeydown);
        });

        // Quick link click tracking
        const quickLinks = document.querySelectorAll(SELECTORS.quickLinks);
        quickLinks.forEach(function (link) {
            link.addEventListener('click', handleQuickLinkClick);
        });
    };

    /**
     * Handles action card click events
     * @param {Event} event - The click event
     * @returns {void}
     */
    const handleCardClick = function (event) {
        const card = event.currentTarget;
        const cardTitle = getCardTitle(card);

        // Track analytics if available
        trackEvent(ANALYTICS_EVENTS.cardClick, {
            cardTitle: cardTitle,
            destination: card.href || card.getAttribute('href')
        });
    };

    /**
     * Handles keyboard navigation for action cards
     * VAPT Compliance: Proper keyboard accessibility
     * @param {KeyboardEvent} event - The keyboard event
     * @returns {void}
     */
    const handleCardKeydown = function (event) {
        // Allow Enter and Space to activate the card
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            event.currentTarget.click();
        }
    };

    /**
     * Handles quick link click events
     * @param {Event} event - The click event
     * @returns {void}
     */
    const handleQuickLinkClick = function (event) {
        const link = event.currentTarget;
        const linkText = sanitizeText(link.textContent);

        trackEvent(ANALYTICS_EVENTS.linkClick, {
            linkText: linkText,
            destination: link.href
        });
    };

    // ============================================
    // PRIVATE METHODS - UTILITIES
    // ============================================

    /**
     * Extracts the card title from a card element
     * @param {HTMLElement} card - The card element
     * @returns {string} The card title text
     */
    const getCardTitle = function (card) {
        const titleElement = card.querySelector('.landing-card-title');
        return titleElement ? sanitizeText(titleElement.textContent) : 'Unknown';
    };

    /**
     * Sanitizes text content to prevent XSS
     * VAPT Compliance: MEDIUM-004 XSS Prevention
     * Clean Code: Single Responsibility - only handles text sanitization
     * @param {string} text - The text to sanitize
     * @returns {string} The sanitized text
     */
    const sanitizeText = function (text) {
        if (typeof text !== 'string') {
            return '';
        }

        // Create a text node to safely encode content
        const div = document.createElement('div');
        div.textContent = text.trim();
        return div.innerHTML;
    };

    // ============================================
    // PRIVATE METHODS - ACCESSIBILITY
    // ============================================

    /**
     * Initializes accessibility features
     * VAPT Compliance: Accessibility improvements for WCAG compliance
     * @returns {void}
     */
    const initializeAccessibility = function () {
        // Ensure all interactive elements have proper focus styles
        const interactiveElements = document.querySelectorAll(
            SELECTORS.actionCards + ', ' + SELECTORS.quickLinks
        );

        interactiveElements.forEach(function (element) {
            // Ensure proper tabindex for keyboard navigation
            if (!element.hasAttribute('tabindex')) {
                element.setAttribute('tabindex', '0');
            }
        });

        // Handle focus-visible polyfill for older browsers
        handleFocusVisible();
    };

    /**
     * Handles focus-visible for browsers that don't support it natively
     * @returns {void}
     */
    const handleFocusVisible = function () {
        // Check if focus-visible is supported
        try {
            document.querySelector(':focus-visible');
        } catch (e) {
            // Add class-based fallback for older browsers
            document.body.classList.add(CSS_CLASSES.focusVisible);
        }
    };

    // ============================================
    // PRIVATE METHODS - ANIMATIONS
    // ============================================

    /**
     * Initializes entrance animations for cards
     * Respects user's reduced motion preference (WCAG 2.3.3)
     * @returns {void}
     */
    const initializeAnimations = function () {
        // Check for reduced motion preference
        if (prefersReducedMotion()) {
            return;
        }

        const cards = document.querySelectorAll(SELECTORS.actionCards);

        cards.forEach(function (card, index) {
            card.style.opacity = '0';
            card.style.transform = 'translateY(20px)';

            setTimeout(function () {
                card.style.transition = 'opacity ' + ANIMATION.fadeInDuration + 'ms ease, transform ' + ANIMATION.fadeInDuration + 'ms ease';
                card.style.opacity = '1';
                card.style.transform = 'translateY(0)';
            }, index * ANIMATION.fadeInDelay);
        });
    };

    /**
     * Checks if user prefers reduced motion
     * WCAG Compliance: Respects user preferences
     * @returns {boolean} True if reduced motion is preferred
     */
    const prefersReducedMotion = function () {
        return window.matchMedia &&
            window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    };

    // ============================================
    // PRIVATE METHODS - LOGO HANDLING
    // ============================================

    /**
     * Initializes logo fallback for error handling
     * Provides graceful degradation if logo fails to load
     * @returns {void}
     */
    const initializeLogoFallback = function () {
        const logoElement = document.querySelector(SELECTORS.heroBadgeLogo);

        if (logoElement) {
            logoElement.addEventListener('error', handleLogoError);
        }
    };

    /**
     * Handles logo loading errors
     * Provides fallback icon if logo fails to load
     * @param {Event} event - The error event
     * @returns {void}
     */
    const handleLogoError = function (event) {
        const logoImg = event.currentTarget;

        // Create fallback icon element
        const fallbackIcon = document.createElement('i');
        fallbackIcon.className = 'bi bi-bank2';
        fallbackIcon.setAttribute('aria-hidden', 'true');

        // Replace failed image with icon
        logoImg.parentNode.replaceChild(fallbackIcon, logoImg);

        if (isDevelopment()) {
            console.warn('LandingPage: Logo failed to load, using fallback icon');
        }
    };

    // ============================================
    // PRIVATE METHODS - ANALYTICS
    // ============================================

    /**
     * Tracks events for analytics (if available)
     * Clean Code: Encapsulates analytics logic
     * @param {string} eventName - The name of the event
     * @param {Object} eventData - Additional event data
     * @returns {void}
     */
    const trackEvent = function (eventName, eventData) {
        try {
            // Check for Google Analytics 4
            if (typeof gtag === 'function') {
                gtag('event', eventName, eventData);
            }

            // Check for legacy Google Analytics
            if (typeof ga === 'function') {
                ga('send', 'event', 'Landing Page', eventName, JSON.stringify(eventData));
            }

            // Log to console in development
            if (isDevelopment()) {
                console.log('Analytics Event:', eventName, eventData);
            }
        } catch (error) {
            // Silently fail - analytics should not break functionality
            console.warn('LandingPage: Analytics tracking failed', error);
        }
    };

    // ============================================
    // PRIVATE METHODS - ENVIRONMENT
    // ============================================

    /**
     * Checks if running in development mode
     * @returns {boolean} True if in development mode
     */
    const isDevelopment = function () {
        return window.location.hostname === 'localhost' ||
            window.location.hostname === '127.0.0.1';
    };

    /**
     * Handles errors in a consistent manner
     * Clean Code: Centralized error handling
     * @param {string} methodName - The name of the method where error occurred
     * @param {Error} error - The error object
     * @returns {void}
     */
    const handleError = function (methodName, error) {
        console.error('LandingPage.' + methodName + ':', error);

        // In production, could send to error tracking service
        if (!isDevelopment() && typeof Sentry !== 'undefined') {
            Sentry.captureException(error, {
                extra: {
                    module: 'LandingPage',
                    method: methodName
                }
            });
        }
    };

    // ============================================
    // PUBLIC API
    // Clean Code: Clear interface definition
    // ============================================

    return {
        /**
         * Initializes the landing page module
         * Call this when DOM is ready
         */
        init: init,

        /**
         * Checks if the module has been initialized
         * @returns {boolean} True if initialized
         */
        isReady: function () {
            return isInitialized;
        }
    };
})();

// ============================================
// DOM READY INITIALIZATION
// Clean Code: Single initialization point
// ============================================

/**
 * Initialize when DOM is ready
 * Using DOMContentLoaded instead of jQuery for better performance
 * VAPT Compliance: External script, no inline code
 */
document.addEventListener('DOMContentLoaded', function () {
    LandingPage.init();
});